import {
  Button,
  CircularProgress,
  Divider,
  Hidden,
  LinearProgress,
  makeStyles,
  MobileStepper,
  Paper,
  Typography,
} from "@material-ui/core";
import { ArrowLeft, ArrowRight } from "@material-ui/icons";
import { Form, Formik } from "formik";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import { AuthContext } from "../../Auth/AuthContextProvider";
import { RegistrationType } from "../../Base/AdoptionConstants";
import useAlerts from "../../Base/AlertProvider";
import { DocumentType, uploadRegistrationDocument } from "../../Base/Services/RegistrationDocumentService";
import { loadRegistrationFromFriendlyId, saveRegistration } from "../../Base/Services/RegistrationService";
import UserService from "../../Base/Services/UserService";
import { AdoptionRegistrationIneligiblePages, AdoptionRegistrationSteps } from "./AdoptionRegistrationSteps";
import {
  getInitialFormValues,
  getInitialFormValuesFromRegistration,
  getRegistrationFromFormValues,
} from "./RegistrationTranslator";
import { ID_DOCUMENT_NAME_1, ID_DOCUMENT_NAME_2 } from "./VerifyIdentityForm";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 750,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(2),
    // Adding marginBottom to make room for stepper when needed
    paddingBottom: theme.spacing(8),
  },
  openButton: {
    float: "right",
    marginTop: theme.spacing(1),
  },
  buttonIconStart: {
    marginRight: theme.spacing(1),
  },
  buttonIconEnd: {
    marginLeft: theme.spacing(1),
  },
  formTitle: {
    fontWeight: 500,
    fontSize: "140%",
  },
  paper: {
    padding: theme.spacing(2),
  },
}));

export default function AdoptionRegistrationForm() {
  const classes = useStyles();
  const formikRef = useRef();
  const stepTitleRef = useRef(null);
  const authContext = React.useContext(AuthContext);
  const { addErrorAlert } = useAlerts();
  const history = useHistory();
  const { userFriendlyId } = useParams();
  const registrationType = useLocation().state?.registrationType;
  const isPaperRegistration = useLocation().state?.isPaperRegistration;
  const [currentFormStep, setCurrentFormStep] = React.useState(AdoptionRegistrationSteps.RegistrationInfo);
  const [validationSchema, setValidationSchema] = React.useState({});
  const [stepHistory, setStepHistory] = React.useState([]);
  const curUserInfo = authContext.userInfo.contactInformation;
  const [initialFormValues, setInitialFormValues] = React.useState(null);

  React.useEffect(() => {
    const onUnload = (e) => {
      if (Boolean(formikRef?.current?.submitCount === 0 && formikRef?.current?.dirty)) {
        e.preventDefault();
        return (e.originalEvent.returnValue = "Are you sure you want to leave? Any of your changes will be lost");
      }
    };

    window.addEventListener("beforeunload", onUnload);

    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  React.useEffect(() => {
    if (userFriendlyId) {
      // Load data from user-friendly-id
      loadRegistrationFromFriendlyId(userFriendlyId).then((registration) =>
        setInitialFormValues(getInitialFormValuesFromRegistration(registration))
      );
    } else if (registrationType) {
      setInitialFormValues(getInitialFormValues(registrationType, curUserInfo, isPaperRegistration));
    } else {
      history.replace("/my-registrations");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (stepTitleRef.current) {
      stepTitleRef.current.focus();
    }
  }, [currentFormStep]);

  function uploadIdentificationDocs(registrationId, registrationFormValues) {
    const uploadPromises = [];
    // Only upload newly selected file (which contains a file size)
    if (registrationFormValues.copyOfId1?.size) {
      uploadPromises.push(
        uploadRegistrationDocument(
          registrationId,
          registrationFormValues.copyOfId1,
          DocumentType.IDENTIFICATION,
          ID_DOCUMENT_NAME_1
        )
      );
    }
    // Only upload newly selected file (which contains a file size)
    if (registrationFormValues.copyOfId2?.size) {
      uploadPromises.push(
        uploadRegistrationDocument(
          registrationId,
          registrationFormValues.copyOfId2,
          DocumentType.IDENTIFICATION,
          ID_DOCUMENT_NAME_2
        )
      );
    }
    return Promise.all(uploadPromises);
  }

  function submitAdoptionRegistration(registrationFormValues) {
    let userPromise;
    // Create new user for paper registration, else update existing user
    if (isPaperRegistration) {
      userPromise = UserService.createNonUtahIdUser({
        contactInformation: registrationFormValues.registrantContactInfo,
      });
    } else {
      userPromise = UserService.updateUser({
        ...authContext.userInfo,
        contactInformation: registrationFormValues.registrantContactInfo,
      });
    }
    return userPromise
      .then((updatedUserInfo) => {
        const registrationToSave = getRegistrationFromFormValues(registrationFormValues);
        if (!isPaperRegistration) {
          authContext.setUserInfo(updatedUserInfo);
        } else {
          registrationToSave.registrant = updatedUserInfo;
        }
        return registrationToSave;
      })
      .then(saveRegistration)
      .then((savedRegistration) => {
        // Save the ID in case there are errors uploading docs - prevent dupe records
        formikRef?.current.setFieldValue("registrationId", savedRegistration.registrationId, false);
        return uploadIdentificationDocs(savedRegistration.registrationId, registrationFormValues).then(
          () => savedRegistration
        );
      })
      .catch((e) => {
        console.error(e);
        addErrorAlert("There was an error submitting your registration");
        throw e;
      })
      .finally(() => formikRef?.current && formikRef.current.setSubmitting(false));
  }

  function clickNext() {
    formikRef.current.validateForm().then((validationErrors) => {
      if (validationErrors && Object.keys(validationErrors).length) {
        const fieldsToTouch = {};
        let errorNameToScrollTo = "";
        for (let errorKey in validationErrors) {
          if (typeof validationErrors[errorKey] === "string") {
            fieldsToTouch[errorKey] = true;
            errorNameToScrollTo = errorNameToScrollTo || errorKey;
          } else {
            fieldsToTouch[errorKey] = {};
            for (let nestedErrorKey in validationErrors[errorKey]) {
              fieldsToTouch[errorKey][nestedErrorKey] = true;
              errorNameToScrollTo = errorNameToScrollTo || `${errorKey}.${nestedErrorKey}`;
            }
          }
        }
        formikRef.current.setTouched(fieldsToTouch, false);

        const fieldsWithErrorName = document.getElementsByName(errorNameToScrollTo);
        if (fieldsWithErrorName.length > 0) {
          fieldsWithErrorName[0].scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        }

        return;
      }
      let nextStep = null;
      switch (currentFormStep) {
        case AdoptionRegistrationSteps.RegistrationInfo:
          nextStep = AdoptionRegistrationSteps.IdentifyingInfo;
          break;
        case AdoptionRegistrationSteps.IdentifyingInfo:
          nextStep = AdoptionRegistrationSteps.AdopteeInfo;
          break;
        case AdoptionRegistrationSteps.AdopteeInfo:
          if (formikRef.current.values.noAdoptedMinorSiblingsInHousehold) {
            nextStep = AdoptionRegistrationIneligiblePages.MinorSiblingIneligibleRegistration;
          } else if (
            formikRef.current.values.registrationType === RegistrationType.ADOPTEE.value &&
            // Ensure adoptee is at least 18 years old
            moment().diff(formikRef.current.values.birthDate || formikRef.current.values.birthDateRangeEnd, "years") <
              18
          ) {
            nextStep = AdoptionRegistrationIneligiblePages.UnderAgeIneligibleRegistration;
          } else {
            nextStep = AdoptionRegistrationSteps.AdoptiveParents;
          }
          break;
        case AdoptionRegistrationSteps.AdoptiveParents:
          nextStep = AdoptionRegistrationSteps.BirthParents;
          break;
        case AdoptionRegistrationSteps.BirthParents:
          nextStep = AdoptionRegistrationSteps.PaymentConfirmation;
          break;
        case AdoptionRegistrationSteps.PaymentConfirmation:
          return formikRef.current.submitForm().then((savedRegistration) => {
            let redirectPath = "/my-registrations";
            if (isPaperRegistration) {
              redirectPath = "/admin";
            }
            return history.push(redirectPath, { newlySavedRegistrationId: savedRegistration.registrationId });
          });
        default:
          console.error("Unknown Step cannot click next!");
          return;
      }
      const updatedHistory = [...stepHistory];
      updatedHistory.push(currentFormStep);
      setStepHistory(updatedHistory);
      setCurrentFormStep(nextStep);
    });
  }

  function clickBack() {
    const history = [...stepHistory];
    const previousStep = history.pop();
    setStepHistory(history);
    setCurrentFormStep(previousStep);
  }

  const canClickBack = React.useMemo(
    () => currentFormStep !== AdoptionRegistrationSteps.RegistrationInfo,
    [currentFormStep]
  );

  const canClickNext = React.useMemo(
    () =>
      ![
        AdoptionRegistrationIneligiblePages.MinorSiblingIneligibleRegistration,
        AdoptionRegistrationIneligiblePages.UnderAgeIneligibleRegistration,
      ].includes(currentFormStep) && !formikRef.current?.isSubmitting,
    [currentFormStep, formikRef]
  );

  React.useEffect(() => {
    setValidationSchema(currentFormStep.validationSchema || {});
    // Scroll to the top of the page after changing pages
    window.scrollTo(0, 0);
  }, [currentFormStep]);

  return (
    <div className={classes.root}>
      {initialFormValues ? (
        <>
          <Formik
            initialValues={initialFormValues}
            innerRef={formikRef}
            validationSchema={validationSchema}
            onSubmit={submitAdoptionRegistration}
          >
            {(formik) => (
              <Form>
                <Paper elevation={1} className={classes.paper}>
                  <Typography align="center" variant="h2" ref={stepTitleRef} tabIndex={-1} style={{ outline: "none" }}>
                    {currentFormStep.title}
                  </Typography>
                  <Divider style={{ marginBottom: 12, marginTop: 12 }} />
                  {React.createElement(currentFormStep.component)}
                  <Hidden xsDown>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-around",
                        alignItems: "center",
                        marginTop: 32,
                      }}
                    >
                      <Button disabled={!canClickBack} onClick={clickBack} hidden={!canClickBack} size="large">
                        <ArrowLeft className={classes.buttonIcon} />
                        Back
                      </Button>
                      <LinearProgress
                        variant="determinate"
                        value={(stepHistory.length / (Object.keys(AdoptionRegistrationSteps).length - 1)) * 100}
                        style={{ width: "50%" }}
                        aria-label="Adoption Registration Progress Bar"
                      />
                      <Button
                        onClick={clickNext}
                        size="large"
                        disabled={!canClickNext || formikRef.current?.isSubmitting}
                      >
                        {currentFormStep === AdoptionRegistrationSteps.PaymentConfirmation ? "Submit" : "Next"}
                        {formikRef.current?.isSubmitting ? (
                          <CircularProgress className={classes.buttonIconEnd} style={{ width: 20, height: 20 }} />
                        ) : (
                          <ArrowRight className={classes.buttonIcon} />
                        )}
                      </Button>
                    </div>
                  </Hidden>
                </Paper>
              </Form>
            )}
          </Formik>
          <Prompt
            when={Boolean(formikRef?.current?.submitCount === 0 && formikRef?.current?.dirty)}
            message={
              "You have not submitted your registration yet! Are you sure you want to leave? Any information you filled out will be not be saved."
            }
          />

          <Hidden smUp>
            <MobileStepper
              position="bottom"
              backButton={
                <Button disabled={!canClickBack} onClick={clickBack}>
                  <ArrowLeft className={classes.buttonIcon} />
                  Back
                </Button>
              }
              nextButton={
                <Button onClick={clickNext} disabled={!canClickNext || formikRef.current?.isSubmitting}>
                  {currentFormStep === AdoptionRegistrationSteps.PaymentConfirmation ? "Submit" : "Next"}{" "}
                  {formikRef.current?.isSubmitting ? (
                    <CircularProgress className={classes.buttonIconEnd} style={{ width: 20, height: 20 }} />
                  ) : (
                    <ArrowRight className={classes.buttonIcon} />
                  )}
                </Button>
              }
              steps={Object.keys(AdoptionRegistrationSteps).length}
              activeStep={stepHistory.length}
              variant="progress"
              aria-label="Adoption Registration Progress Bar"
            />
          </Hidden>
        </>
      ) : (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </div>
      )}
    </div>
  );
}
